import React from 'react';
import { Phone, ArrowRight, ShieldCheck, Clock, MapPin, Star, Truck, CheckCircle2 } from 'lucide-react';
import { FadeIn } from './Animations';
export const Hero = () => {
  return <section className="relative min-h-[100vh] flex items-center text-white overflow-hidden" data-id="element-272">
      {/* Background image */}
      <div className="absolute inset-0 z-0" data-id="element-273">
        <img src="https://cdn.magicpatterns.com/uploads/9xtkJAr7hK44a2vw9qRwjt/JEZ1.png" alt="Jez Junk Away Dump Trailer" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-274" />
        {/* Dark overlays for readability */}
        <div className="absolute inset-0 bg-brand-black/70" data-id="element-275" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/80 to-brand-black/40" data-id="element-276" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-transparent to-brand-black/50" data-id="element-277" />
      </div>

      {/* Red accent line */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-brand-red z-10" data-id="element-278" />

      {/* Content */}
      <div className="relative z-10 w-full pt-36 pb-24 md:pt-44 md:pb-32" data-id="element-279">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-280">
          <div className="max-w-3xl" data-id="element-281">
            <FadeIn data-id="element-282">
              <div className="inline-flex items-center gap-2 bg-brand-red/20 backdrop-blur border border-brand-red/40 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider w-fit mb-6" data-id="element-283">
                <span className="w-2 h-2 bg-brand-red rounded-full animate-pulse" data-id="element-284" />
                Same-Day Service Available
              </div>
            </FadeIn>

            <FadeIn delay={0.05} data-id="element-285">
              <h1 className="font-oswald text-5xl md:text-7xl lg:text-8xl font-bold uppercase leading-[1] tracking-tight drop-shadow-2xl" data-id="element-286">
                Stop Staring at{' '}
                <span className="text-brand-red" data-id="element-287">That Pile.</span>
                <br data-id="element-288" />
                One Call. <span className="text-brand-red" data-id="element-289">Gone.</span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-290">
              <p className="text-lg md:text-2xl text-zinc-200 max-w-2xl leading-relaxed mt-6 drop-shadow-lg" data-id="element-291">
                The Central Valley's most trusted junk and debris removal team.{' '}
                <span className="text-white font-semibold" data-id="element-292">
                  We do all the lifting, loading, and hauling.
                </span>{' '}
                You enjoy the clean space.
              </p>
            </FadeIn>

            <FadeIn delay={0.15} data-id="element-293">
              <div className="flex flex-wrap items-center gap-2 text-sm mt-6" data-id="element-294">
                <div className="flex" data-id="element-295">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-296" />)}
                </div>
                <span className="font-bold text-white" data-id="element-297">5.0</span>
                <span className="text-zinc-300" data-id="element-298">
                  · Trusted by 500+ Central Valley homes & businesses
                </span>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="flex flex-col sm:flex-row gap-3 mt-8" data-id="element-299">
              <a href="tel:+15597709442" className="group flex items-center justify-center gap-3 bg-brand-red hover:bg-brand-darkred text-white px-8 py-5 rounded-sm font-bold text-lg transition-all uppercase tracking-wide shadow-2xl shadow-brand-red/40 hover:shadow-brand-red/60 hover:scale-[1.02] duration-200" data-id="element-300">
                <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform" data-id="element-301" />
                Call (559) 770-9442
              </a>
              <a href="#services" className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/30 px-8 py-5 rounded-sm font-bold text-lg transition-colors uppercase tracking-wide" data-id="element-302">
                Free Estimate
                <ArrowRight className="w-5 h-5" data-id="element-303" />
              </a>
            </FadeIn>

            <FadeIn delay={0.25} className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 pt-8 border-t border-white/20 max-w-3xl" data-id="element-304">
              {[{
              icon: ShieldCheck,
              label: 'Licensed',
              sub: '& Insured'
            }, {
              icon: Clock,
              label: 'Open 7 Days',
              sub: '7AM - 7PM'
            }, {
              icon: MapPin,
              label: 'Central Valley',
              sub: 'Local Team'
            }, {
              icon: Phone,
              label: 'Same-Day',
              sub: 'Available'
            }].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-305">
                  <item.icon className="w-5 h-5 text-brand-red shrink-0 mt-0.5" data-id="element-306" />
                  <div data-id="element-307">
                    <p className="font-oswald font-bold text-white text-sm uppercase leading-tight" data-id="element-308">
                      {item.label}
                    </p>
                    <p className="text-xs text-zinc-400" data-id="element-309">{item.sub}</p>
                  </div>
                </div>)}
            </FadeIn>
          </div>
        </div>
      </div>

      {/* Stats strip overlay at bottom */}
      <div className="absolute bottom-1 left-0 right-0 z-10 hidden md:block" data-id="element-310">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" data-id="element-311">
          <div className="grid grid-cols-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-sm overflow-hidden" data-id="element-312">
            {[{
            icon: Truck,
            num: '500+',
            label: 'Jobs Completed'
          }, {
            icon: Star,
            num: '5.0',
            label: 'Customer Rating'
          }, {
            icon: Clock,
            num: '24/7',
            label: 'Free Estimates'
          }, {
            icon: CheckCircle2,
            num: '100%',
            label: 'Satisfaction'
          }].map((stat, i) => <div key={i} className="p-5 flex items-center gap-4 border-r border-white/10 last:border-r-0" data-id="element-313">
                <stat.icon className="w-7 h-7 text-brand-red shrink-0" data-id="element-314" />
                <div data-id="element-315">
                  <p className="font-oswald text-3xl font-bold text-white leading-none" data-id="element-316">
                    {stat.num}
                  </p>
                  <p className="text-xs text-zinc-300 font-semibold uppercase tracking-wider mt-1" data-id="element-317">
                    {stat.label}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};